import { taskTypeOwnsCargoItems } from './cargoPick';
import { UNIT_IDENTIFIER_FAMILIES } from './cargoCascade';
import type { CargoFamily, MockTask } from './types';

/**
 * What a NEW task inherits from the legs already on the order. The
 * counterpart to the downstream cascade in `cargoCascade.ts`: a new task
 * is appended at the end of the job, so there is nothing after it to
 * push to — it pulls from what came before instead.
 *
 * <p>
 * Both helpers scan BACKWARDS for the most recent leg that actually
 * carries a value, rather than reading the single last task. The last
 * task is often a Stop or an Inspection with no cargo state at all, and
 * a blank default there would be worse than no feature.
 * </p>
 */

/** Non-cancelled tasks, latest sequence first. */
function legsNewestFirst(tasks: MockTask[]): MockTask[] {
  return tasks
    .filter((t) => t._source != null && t._source.state !== 'Cancelled')
    .sort((a, b) => (b._source?.sequence ?? 0) - (a._source?.sequence ?? 0));
}

/**
 * Per-task temperature to seed a new task with, or null.
 *
 * <p>
 * Only consulted when the ORDER header carries no temperature — the
 * header is the authoritative spec and stays the primary default. This
 * covers the case where the spec was never entered at the top but the
 * operator has been setting each leg by hand: without it, every new leg
 * starts blank and has to be retyped.
 * </p>
 */
export function inheritedTemperatureC(tasks: MockTask[]): number | null {
  for (const task of legsNewestFirst(tasks)) {
    const value = task._source?.temperatureC;
    if (value != null) return value;
  }
  return null;
}

/**
 * Unit identifiers (BIC code / Tank ID) to seed a new task's cargo rows
 * with, keyed by 1-based `itemIndex`.
 *
 * <p>
 * Taken from the most recent leg carrying any identifier — the same
 * physical box or barrel continues onto the leg being added. Empty for
 * families where the cargo item isn't a re-usable unit (a pallet line
 * has no identity to carry forward).
 * </p>
 *
 * <p>
 * For container / tank this now reads process events too, not just the
 * handling legs: once a wash or an inspection can name its barrel, it is
 * the most recent statement of which unit is on the job, and skipping it
 * would seed a new task from a staler leg.
 * </p>
 */
export function inheritedUnitIdentifiers(
  tasks: MockTask[],
  family: CargoFamily,
): Map<number, string> {
  const out = new Map<number, string>();
  if (!UNIT_IDENTIFIER_FAMILIES.has(family)) return out;

  for (const task of legsNewestFirst(tasks)) {
    const src = task._source;
    if (!src || !taskTypeOwnsCargoItems(family, src.type)) continue;
    for (const item of src.cargoItems ?? []) {
      const value = (item.identifier ?? '').trim();
      if (value === '') continue;
      // First writer wins: we're walking newest-first, so an earlier leg
      // never overwrites what a later one already contributed.
      if (!out.has(item.itemIndex)) out.set(item.itemIndex, value);
    }
    if (out.size > 0) return out;
  }
  return out;
}
